import React, { useState } from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useFeatureGate, FEATURE_KEYS } from '@/context/SubscriptionContext'
import { StudioArt, StudioPin } from '../../types'
import { FRAME_FINISHES, FRAME_PROFILE_OPTIONS, MAT_TONES, WALL_NAME } from '../../constants'
import { printHeight } from '../../babylon/sceneMath'
import { SegChips, STUDIO_ACCENT, StudioSlider, Swatch } from '../shared'
import { CLASSIC_FRAME_OPTIONS, DEFAULT_CLASSIC_FRAME_ID, DEFAULT_CLASSIC_MAT_W } from '../../classicCatalog'
import { FinishColorPanel } from './FinishColorPanel'
import {
  AccentLink,
  DangerLink,
  FrameChip,
  FrameRow,
  Head,
  isCustomTone,
  Kind,
  Note,
  ProPickerBadge,
  ProPickerBtn,
  Root,
  Scroll,
  Section,
  SectionLabel,
  Sub,
  SwatchGrid,
  TitleInput,
} from './InspectorShared'

const MAT_TONE_HEXES = MAT_TONES.map((t) => t.hex)

export interface PrintInspectorProps {
  art: StudioArt
  onRename: (name: string) => void
  onUpdate: (patch: Partial<StudioArt>) => void
  onRemove: () => void
  onOpenLightbox: () => void
  /**
   * True for a print inside a Classic room — its slot mesh is a single
   * baked-geometry object loaded straight from the legacy `.babylon` file
   * (see useStudioLegacyRoom), with fixed placement and no per-print mat or
   * spotlight. Frame *is* real and per-print here too — a real model from
   * `Assets.frames` (see `CLASSIC_FRAME_OPTIONS`/`art.classicFrameId`)
   * rather than the procedural profile/finish studio prints use. Photo
   * swap, rename, and story still work identically either way.
   */
  locked?: boolean
  /** Push undo snapshot after discrete edits / slider pointer-up. */
  onHistoryCommit?: () => void
  /** Story Layer pins bound to this print (`artId`). */
  storyPins?: StudioPin[]
  /** Story Layer visible in the room — ◈ reveals the sticky when on. */
  showPins?: boolean
  /** Click ◈ — reveal sticky in the room (and open compose when host). */
  onRevealPins?: () => void
}

const StoryStats = styled(Box)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.06em',
  color: 'var(--ds-night-ink-muted)',
  marginBottom: 12,
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 0,
})

const StoryPinBtn = styled('button')({
  all: 'unset',
  boxSizing: 'border-box',
  cursor: 'pointer',
  color: STUDIO_ACCENT.gold,
  fontFamily: 'inherit',
  fontSize: 'inherit',
  letterSpacing: 'inherit',
  padding: '2px 0',
  borderRadius: 3,
  transition: 'color 120ms ease',
  '&:hover': { color: '#ffe2a8' },
  '&:disabled': { cursor: 'default', color: 'var(--ds-night-ink-muted)', opacity: 0.7 },
})

/** Inspector shown for the selected print — placement, frame, mat, spotlight, story. */
export function PrintInspector({
  art,
  onRename,
  onUpdate,
  onRemove,
  onOpenLightbox,
  locked,
  onHistoryCommit,
  storyPins = [],
  showPins = true,
  onRevealPins,
}: PrintInspectorProps) {
  const ph = printHeight(art)
  const commit = () => onHistoryCommit?.()
  const patch = (p: Partial<StudioArt>) => {
    onUpdate(p)
    commit()
  }
  const pinCount = storyPins.length
  const { isFeatureAvailable, showUpgrade } = useFeatureGate()
  const hasCustomFinishes = isFeatureAvailable(FEATURE_KEYS.STUDIO_CUSTOM_FINISHES)
  const [matPanelOpen, setMatPanelOpen] = useState(false)
  const applyCustomMat = (hex: string) => {
    if (!hasCustomFinishes) {
      showUpgrade({ featureKey: FEATURE_KEYS.STUDIO_CUSTOM_FINISHES, headline: 'Custom finishes are a Gallery+ perk' })
      return
    }
    patch({ matTone: hex })
  }

  return (
    <Root>
      <Head>
        <Kind>PRINT</Kind>
        <TitleInput value={art.name} spellCheck={false} onChange={(e) => onRename(e.target.value)} />
        <Sub>{locked ? 'FIXED SLOT — CLASSIC ROOM' : `${WALL_NAME[art.side]} · ${art.w.toFixed(1)} × ${ph.toFixed(1)} M`}</Sub>
      </Head>
      <Scroll>
        {locked ? (
          <>
            <Section>
              <Note>Placement is fixed by the loaded Classic room — no per-print spotlight here, but frame and mat are both real and yours to pick per photo.</Note>
            </Section>
            <Section>
              <SectionLabel>FRAME</SectionLabel>
              <FrameRow>
                {CLASSIC_FRAME_OPTIONS.map((f) => {
                  const isOn = (art.classicFrameId ?? DEFAULT_CLASSIC_FRAME_ID) === f.id
                  return (
                    <FrameChip
                      key={f.id}
                      type='button'
                      $on={isOn}
                      title={f.name}
                      onClick={() => patch({ classicFrameId: f.id })}
                    >
                      <img src={f.thumbUrl} alt={f.name} width={30} height={30} style={{ objectFit: 'contain' }} />
                    </FrameChip>
                  )
                })}
              </FrameRow>
            </Section>
            <Section>
              <SectionLabel>MATTING</SectionLabel>
              <Note>Mat width between the frame and the photo. Best with a frame selected.</Note>
              <StudioSlider
                label='Mat width'
                value={art.classicMatW ?? DEFAULT_CLASSIC_MAT_W}
                min={0}
                max={0.24}
                step={0.01}
                onChange={(v) => onUpdate({ classicMatW: v })}
                onCommit={commit}
                valueFormatter={(v) => `${Math.round(v * 100)}%`}
              />
              <SwatchGrid>
                {MAT_TONES.map((t) => (
                  <Swatch
                    key={t.hex}
                    color={t.hex}
                    size={24}
                    label={t.name}
                    selected={art.matTone === t.hex}
                    onClick={() => patch({ matTone: t.hex })}
                  />
                ))}
                <ProPickerBtn
                  type='button'
                  data-finish-trigger='mat'
                  aria-label='Custom mat color'
                  title='Custom color · Gallery+'
                  $selected={isCustomTone(art.matTone, MAT_TONE_HEXES) || matPanelOpen}
                  onClick={() => setMatPanelOpen((v) => !v)}
                >
                  <ProPickerBadge>PRO</ProPickerBadge>
                </ProPickerBtn>
              </SwatchGrid>
            </Section>
          </>
        ) : (
          <>
            <Section>
              <SectionLabel>PLACEMENT</SectionLabel>
              <StudioSlider
                label='Width'
                value={art.w}
                min={0.8}
                max={3.6}
                step={0.01}
                onChange={(v) => onUpdate({ w: v })}
                onCommit={commit}
                valueFormatter={(v) => `${v.toFixed(2)} M`}
              />
              <StudioSlider
                label='Eye line'
                value={art.y}
                min={1.5}
                max={3.3}
                step={0.01}
                onChange={(v) => onUpdate({ y: v })}
                onCommit={commit}
                valueFormatter={(v) => `${v.toFixed(2)} M`}
              />
              <StudioSlider
                label='Slide'
                value={art.u}
                min={-5.6}
                max={5.6}
                step={0.1}
                onChange={(v) => onUpdate({ u: v })}
                onCommit={commit}
                valueFormatter={(v) => `${v.toFixed(1)} M`}
              />
            </Section>

            <Section>
              <SectionLabel>FRAME</SectionLabel>
              <SegChips
                options={FRAME_PROFILE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                value={art.profile}
                onChange={(v) => patch({ profile: v as StudioArt['profile'] })}
              />
              <SwatchGrid>
                {FRAME_FINISHES.map((f) => (
                  <Swatch
                    key={f.hex}
                    color={f.hex}
                    size={24}
                    label={f.name}
                    selected={art.finish === f.hex}
                    onClick={() => patch({ finish: f.hex })}
                  />
                ))}
              </SwatchGrid>
            </Section>

            <Section>
              <SectionLabel>MAT</SectionLabel>
              <StudioSlider
                label='Width'
                value={art.matW}
                min={0}
                max={0.14}
                step={0.01}
                onChange={(v) => onUpdate({ matW: v })}
                onCommit={commit}
                valueFormatter={(v) => `${Math.round(v * 100)} CM`}
              />
              <SwatchGrid>
                {MAT_TONES.map((t) => (
                  <Swatch
                    key={t.hex}
                    color={t.hex}
                    size={24}
                    label={t.name}
                    selected={art.matTone === t.hex}
                    onClick={() => patch({ matTone: t.hex })}
                  />
                ))}
                <ProPickerBtn
                  type='button'
                  data-finish-trigger='mat'
                  aria-label='Custom mat color'
                  title='Custom color · Gallery+'
                  $selected={isCustomTone(art.matTone, MAT_TONE_HEXES) || matPanelOpen}
                  onClick={() => setMatPanelOpen((v) => !v)}
                >
                  <ProPickerBadge>PRO</ProPickerBadge>
                </ProPickerBtn>
              </SwatchGrid>
            </Section>

            <Section>
              <SectionLabel>SPOTLIGHT</SectionLabel>
              <StudioSlider
                label='Intensity'
                value={art.spotI}
                min={0}
                max={16}
                step={0.1}
                onChange={(v) => onUpdate({ spotI: v })}
                onCommit={commit}
                valueFormatter={(v) => v.toFixed(1)}
              />
              <StudioSlider
                label='Warmth'
                value={art.warm}
                min={0}
                max={1}
                step={0.01}
                onChange={(v) => onUpdate({ warm: v })}
                onCommit={commit}
                valueFormatter={(v) => `${Math.round(v * 100)}%`}
              />
            </Section>
          </>
        )}

        <Section>
          <SectionLabel>STORY</SectionLabel>
          <StoryStats>
            ❤ {art.loves || 0}
            <Box component='span' sx={{ mx: 0.75 }}>
              ·
            </Box>
            💬 {art.commentsCount || 0}
            {pinCount > 0 && (
              <>
                <Box component='span' sx={{ mx: 0.75 }}>
                  ·
                </Box>
                <StoryPinBtn
                  type='button'
                  title={showPins ? 'Show pinned note in room' : 'Turn on Story Layer to see notes'}
                  disabled={!onRevealPins}
                  onClick={() => onRevealPins?.()}
                >
                  ◈ {pinCount} PINNED
                </StoryPinBtn>
              </>
            )}
          </StoryStats>
          <AccentLink onClick={onOpenLightbox}>View large</AccentLink>
        </Section>

        {!locked && (
          <Section>
            <DangerLink
              onClick={() => {
                onRemove()
                commit()
              }}
            >
              Remove from wall
            </DangerLink>
          </Section>
        )}
      </Scroll>

      {matPanelOpen && (
        <FinishColorPanel
          surface='mat'
          initialColor={art.matTone}
          showProStrip={!hasCustomFinishes}
          onApply={applyCustomMat}
          onClose={() => setMatPanelOpen(false)}
          onUpgrade={() => {
            showUpgrade({ featureKey: FEATURE_KEYS.STUDIO_CUSTOM_FINISHES, headline: 'Custom finishes are a Gallery+ perk' })
          }}
        />
      )}
    </Root>
  )
}
